import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import type { SettingsResponse } from "@/generated/api";
import {
    applyChange,
    applyToDevice,
    documentFromDevice,
    missingFrom,
    type SettingsDocument,
} from "@/core/settingsDocument";

type SettingsApi = Pick<ApiClient, "get" | "patch">;

interface SettingsAuth {
    readonly status: "unknown" | "anonymous" | "authenticated";
    readonly isSignedIn: boolean;
}

/**
 * The listener's settings. Signed in, they are the account's (`/me/settings`), and the device follows them so the
 * player and the colour scheme use them at once. Signed out, they are the device's. At sign-in what the device
 * chose and the account does not have is copied to the account; where the account already has a value, it wins.
 */
export class SettingsStore {
    document: SettingsDocument = {};
    loaded = false;

    private generation = 0;

    constructor(
        private readonly client: SettingsApi,
        private readonly auth: SettingsAuth
    ) {
        makeAutoObservable<SettingsStore, "client" | "auth" | "generation">(this, {
            client: false,
            auth: false,
            generation: false,
        });

        reaction(
            () => this.auth.status,
            (status) => {
                this.generation++;
                if (status === "authenticated") {
                    void this.signedIn();
                } else if (status === "anonymous") {
                    this.document = documentFromDevice();
                    this.loaded = true;
                }
            },
            { fireImmediately: true }
        );
    }

    private async signedIn() {
        const generation = this.generation;
        const device = documentFromDevice();
        try {
            const account = await this.client.get<SettingsResponse>("/me/settings");
            const missing = missingFrom(account.settings as Record<string, unknown>, device);
            const after =
                Object.keys(missing).length > 0
                    ? await this.client.patch<SettingsResponse>("/me/settings", missing)
                    : account;
            runInAction(() => {
                if (generation !== this.generation) return;
                this.document = after.settings as SettingsDocument;
                this.loaded = true;
                applyToDevice(this.document);
            });
        } catch (error) {
            // Not fatal: the device's settings stand until the next sign-in
            console.warn("Could not load the settings", error);
            runInAction(() => {
                if (generation !== this.generation) return;
                this.document = device;
                this.loaded = true;
            });
        }
    }

    /**
     * Changes some settings. A key given as null goes back to the app's default. Shown at once; signed in, it is
     * saved to the account, and if the account refuses it the account's own settings are read again.
     */
    async change(change: Record<string, unknown>) {
        const generation = this.generation;
        const next = applyChange(this.document, change) as SettingsDocument;
        this.document = next;
        applyToDevice(next);
        if (!this.auth.isSignedIn) return;

        try {
            const after = await this.client.patch<SettingsResponse>("/me/settings", change);
            runInAction(() => {
                if (generation !== this.generation) return;
                this.document = after.settings as SettingsDocument;
                applyToDevice(this.document);
            });
        } catch (error) {
            console.warn("Could not save the settings", error);
            await this.reread(generation);
        }
    }

    private async reread(generation: number) {
        try {
            const account = await this.client.get<SettingsResponse>("/me/settings");
            runInAction(() => {
                if (generation !== this.generation) return;
                this.document = account.settings as SettingsDocument;
                applyToDevice(this.document);
            });
        } catch (error) {
            console.warn("Could not read the settings", error);
        }
    }
}
